// M323 (2026-05-30) — Admin nhập thông báo popup cho NV.
// Form: bật/tắt + tiêu đề + nội dung + thời lượng hiện (phút) + xem trước trực quan.
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import AdminPageHeader from '../components/AdminPageHeader.jsx';

// Gợi ý nội dung mặc định (Owner có thể sửa) — về 129 câu nội quy mới M322.
const SUGGEST_TITLE = 'Câu hỏi nội quy mới trong bộ 1000K';
const SUGGEST_BODY =
  'Cửa hàng vừa bổ sung 129 câu hỏi mới về nội quy (Chương 5 đến Chương 22) vào bộ câu hỏi 1000K.\n\n' +
  'Tất cả nhân viên cần hoàn thành các câu hỏi này TRONG THÁNG NÀY thì mới được tính là ĐẠT.\n\n' +
  'Hãy vào mục học/ôn bộ 1000K để hoàn thành nhé!';

export default function AdminAnnouncementPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [minutes, setMinutes] = useState(4);

  useEffect(() => {
    let cancelled = false;
    api.adminGetAnnouncement()
      .then((a) => {
        if (cancelled) return;
        setEnabled(!!a.enabled);
        // Lần đầu chưa có nội dung → điền gợi ý sẵn cho Owner.
        setTitle(a.title || SUGGEST_TITLE);
        setBody(a.body || SUGGEST_BODY);
        setMinutes(Math.max(1, Math.round((Number(a.durationSeconds) || 240) / 60)));
      })
      .catch(() => {
        if (cancelled) return;
        setTitle(SUGGEST_TITLE);
        setBody(SUGGEST_BODY);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function save() {
    if (enabled && (!title.trim() || !body.trim())) {
      toast.error('Cần nhập tiêu đề và nội dung khi bật thông báo');
      return;
    }
    const durationSeconds = Math.max(60, Math.min(3600, Math.round(minutes * 60)));
    setSaving(true);
    try {
      await api.adminSaveAnnouncement({
        enabled,
        title: title.trim(),
        body: body.trim(),
        durationSeconds,
      });
      toast.success(enabled
        ? '✓ Đã lưu & BẬT thông báo. NV sẽ thấy popup khi đăng nhập.'
        : '✓ Đã lưu. Thông báo đang TẮT (NV không thấy).');
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-full bg-slate-50 py-8 px-4">
        <div className="max-w-5xl mx-auto text-sm text-slate-500 text-center">Đang tải…</div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 py-6 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <AdminPageHeader
          title="📢 Thông báo cho nhân viên"
          subtitle="Soạn 1 thông báo hiện popup góc phải màn hình NV khi đăng nhập, tự tắt sau ít phút."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          {/* Form bên trái */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            {/* Bật/tắt */}
            <button
              type="button"
              onClick={() => setEnabled(v => !v)}
              data-testid="ann-toggle"
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition ${
                enabled
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                  : 'bg-slate-50 border-slate-300 text-slate-600'
              }`}
            >
              <span className="text-sm font-semibold">
                {enabled ? '🟢 Đang BẬT — NV sẽ thấy thông báo' : '⚪ Đang TẮT — NV không thấy'}
              </span>
              <span className={`w-11 h-6 rounded-full relative transition ${enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${enabled ? 'left-[22px]' : 'left-0.5'}`} />
              </span>
            </button>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Tiêu đề</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                data-testid="ann-title"
                placeholder="VD: Câu hỏi nội quy mới trong bộ 1000K"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-amber-400 focus:outline-none"
              />
              <div className="text-[11px] text-slate-400 mt-1 text-right">{title.length}/200</div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nội dung</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={2000}
                rows={7}
                data-testid="ann-body"
                placeholder="Nội dung thông báo… (xuống dòng được)"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-amber-400 focus:outline-none resize-y"
              />
              <div className="text-[11px] text-slate-400 mt-1 text-right">{body.length}/2000</div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Thời gian tự tắt (phút)</label>
              <input
                type="number"
                min={1}
                max={60}
                value={minutes}
                onChange={(e) => setMinutes(Math.max(1, Math.min(60, Number(e.target.value) || 1)))}
                data-testid="ann-minutes"
                className="w-28 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-amber-400 focus:outline-none"
              />
              <span className="text-xs text-slate-400 ml-2">phút (mặc định 4)</span>
            </div>

            <div className="pt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="ann-save"
                className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {saving ? 'Đang lưu…' : '💾 Lưu thông báo'}
              </button>
              <button
                type="button"
                onClick={() => { setTitle(SUGGEST_TITLE); setBody(SUGGEST_BODY); }}
                className="px-4 py-2.5 rounded-lg text-sm border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Điền mẫu gợi ý
              </button>
            </div>
          </div>

          {/* Xem trước bên phải */}
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-2">Xem trước (NV sẽ thấy như vầy):</div>
            <div className="bg-slate-100 rounded-2xl border border-slate-200 p-6 min-h-[220px] flex items-end justify-end">
              <div className="w-[340px] max-w-full">
                <div className="rounded-2xl border border-amber-200 bg-white shadow-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 flex items-center gap-2">
                    <span className="text-lg">📢</span>
                    <span className="text-white font-semibold text-sm flex-1 leading-snug">{title || '(Chưa có tiêu đề)'}</span>
                    <span className="text-white/90 text-lg leading-none px-1">×</span>
                  </div>
                  <div className="px-4 py-3">
                    <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{body || '(Chưa có nội dung)'}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">Tự đóng sau {minutes}:00</span>
                      <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">Đã hiểu</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3 leading-relaxed">
              💡 Thông báo hiện ở góc phải dưới màn hình NV, chỉ hiện <b>1 lần mỗi phiên đăng nhập</b> cho mỗi bản thông báo
              (NV bấm "Đã hiểu" hoặc chờ tự tắt). Mỗi lần anh lưu bản mới, NV sẽ thấy lại ở lần đăng nhập kế tiếp.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
